import type { Office } from "./types"

export const OXFORD_SPECIALISMS = [
  "Residential Sales",
  "Lettings",
  "Property Management",
  "New Homes",
  "Commercial",
  "Land & Development",
  "Auctions",
  "Relocation",
] as const

export const OXFORD_AREAS = [
  "Oxford City Centre",
  "Summertown",
  "Jericho",
  "Headington",
  "Cowley",
  "Iffley",
  "Botley",
  "Wolvercote",
  "Marston",
  "North Oxford",
  "East Oxford",
  "Abingdon Road",
  "Osney",
  "Grandpont",
  "Old Marston",
  "Littlemore",
  "Rose Hill",
  "Cutteslowe",
] as const

export const OXFORD_OFFICES: Office[] = [
  {
    slug: "savills-oxford",
    name: "Savills",
    branch: "Oxford",
    address: "Wytham Court, 11 West Way, Botley",
    postcode: "OX2 0QL",
    phone: "01865 339700",
    website: "https://www.savills.co.uk/estate-agents/south-east-england/savills-oxford.aspx",
    email: "oxford@savills.com",
    description:
      "Savills Oxford operates from their Botley office covering residential sales and lettings across the city, surrounding villages, and the Oxfordshire countryside. With a team of experienced agents and access to the global Savills network, they are a leading choice for premium property in one of England's most sought-after cities.",
    established: 1855,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development"],
    areas: ["North Oxford", "Summertown", "Jericho", "Headington", "Wolvercote", "Botley"],
    propertyCount: 70,
    avgPrice: "£895,000",
    rating: 4.5,
    reviewCount: 124,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Founded in 1855, Savills is one of the world's most established property brands, and their Oxford office in Botley brings that pedigree to one of England's most competitive housing markets. The team covers residential sales and lettings across North Oxford, Summertown, Jericho, Headington, Wolvercote, and Botley, with access to the global Savills network.\n\nWith an average listing price of £895,000 and around 70 properties typically on their books, this is an office firmly positioned at the upper end of the Oxford market. Their particular strength lies in connecting sellers with qualified buyers from across the UK and internationally.\n\nThe combination of deep local knowledge and a worldwide reach makes Savills Oxford a natural choice for those buying or selling premium property in the city and surrounding Oxfordshire villages.",
    whyChoose: [
      "Over 165 years of estate agency heritage",
      "Rated 4.5/5 from 124 client reviews",
      "Specialists in premium Oxford property with an average listing price of £895,000",
      "Global buyer network paired with genuine local Oxfordshire expertise",
    ],
    faq: [
      {
        question: "What areas does Savills Oxford cover?",
        answer:
          "Savills Oxford covers North Oxford, Summertown, Jericho, Headington, Wolvercote, and Botley, along with the wider Oxfordshire countryside and surrounding villages.",
      },
      {
        question: "What services does Savills Oxford offer?",
        answer:
          "Savills Oxford provides residential sales, lettings, new homes, and land and development services. Their team specialises in premium properties across the city and rural Oxfordshire.",
      },
      {
        question: "What are Savills Oxford's opening hours?",
        answer:
          "Savills Oxford is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "knight-frank-oxford",
    name: "Knight Frank",
    branch: "Oxford",
    address: "267 Banbury Road, Summertown",
    postcode: "OX2 7HT",
    phone: "01865 790077",
    website: "https://www.knightfrank.co.uk/offices/oxford-estate-agents",
    email: "oxford@knightfrank.com",
    description:
      "Knight Frank's Oxford office is situated on Banbury Road in the heart of Summertown, one of the city's most desirable areas. Their team handles premium residential sales and country property across Oxford and the wider Oxfordshire region, bringing international reach and deep local expertise to every instruction.",
    established: 1896,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development", "Relocation"],
    areas: ["North Oxford", "Summertown", "Wolvercote", "Jericho", "Headington", "Cutteslowe"],
    propertyCount: 55,
    avgPrice: "£1,050,000",
    rating: 4.7,
    reviewCount: 92,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Knight Frank has been setting the standard in property since 1896, and their Oxford office on Banbury Road in Summertown reflects that heritage. Positioned in one of the city's most desirable neighbourhoods, the team handles premium residential sales and country property across Oxford and wider Oxfordshire, with an average listing price of £1,050,000.\n\nThe office covers North Oxford, Summertown, Wolvercote, Jericho, Headington, and Cutteslowe, offering breadth of service that includes sales, lettings, new homes, land, and relocation. Their international network connects Oxford sellers with qualified buyers across the globe.\n\nWith a 4.7-star rating from 92 reviews and a reputation built on discretion, market knowledge, and results, Knight Frank Oxford is the natural choice for those operating at the top end of the city's property market.",
    whyChoose: [
      "Rated 4.7/5 from 92 client reviews",
      "Average listing price of £1,050,000, reflecting prime market focus",
      "Full service: sales, lettings, new homes, land, and relocation",
      "International buyer network with deep local Oxford knowledge",
    ],
    faq: [
      {
        question: "What areas does Knight Frank Oxford cover?",
        answer:
          "Knight Frank Oxford covers North Oxford, Summertown, Wolvercote, Jericho, Headington, and Cutteslowe, with a wider reach across Oxfordshire for country properties and rural estates.",
      },
      {
        question: "What services does Knight Frank Oxford offer?",
        answer:
          "Knight Frank Oxford provides residential sales, lettings, new homes, land and development, and relocation services. They specialise in premium residential property and country homes across Oxfordshire.",
      },
      {
        question: "What are Knight Frank Oxford's opening hours?",
        answer:
          "Knight Frank Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "finders-keepers-summertown",
    name: "Finders Keepers",
    branch: "Summertown",
    address: "258 Banbury Road, Summertown",
    postcode: "OX2 7DE",
    phone: "01865 311011",
    website: "https://www.ffreslettings.co.uk",
    email: "summertown@finderskeepers.co.uk",
    description:
      "Finders Keepers is Oxford's best-known independent letting agent, with roots going back over 40 years. Their Summertown office is the flagship of their multi-branch operation, managing a vast portfolio of rental properties across the city. Known for their thorough tenant referencing and proactive property management.",
    established: 1976,
    specialisms: ["Lettings", "Property Management"],
    areas: ["Summertown", "North Oxford", "Wolvercote", "Cutteslowe", "Jericho", "Marston"],
    propertyCount: 250,
    avgPrice: "£1,600/month",
    rating: 4.3,
    reviewCount: 342,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–12:30" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Finders Keepers has been a fixture of the Oxford lettings market since 1976, and their Summertown flagship office on Banbury Road is at the centre of that reputation. Managing around 250 properties with an average rent of £1,600 per month, this is one of the city's largest and most experienced letting agencies.\n\nThe team covers Summertown, North Oxford, Wolvercote, Cutteslowe, Jericho, and Marston, with particular expertise in properties suited to academics, professionals, and families. Their thorough tenant referencing process and proactive property management have earned them a loyal client base over nearly five decades.\n\nWith 342 reviews and a 4.3-star rating, Finders Keepers Summertown is a trusted name for landlords and tenants who value reliability and local knowledge above all.",
    whyChoose: [
      "Nearly 50 years of Oxford lettings experience since 1976",
      "Rated 4.3/5 from 342 client reviews",
      "Approximately 250 managed properties across North Oxford",
      "Thorough tenant referencing and proactive property management",
    ],
    faq: [
      {
        question: "What areas does Finders Keepers Summertown cover?",
        answer:
          "Finders Keepers Summertown covers Summertown, North Oxford, Wolvercote, Cutteslowe, Jericho, and Marston, with a focus on quality rental properties in the northern part of the city.",
      },
      {
        question: "What services does Finders Keepers Summertown offer?",
        answer:
          "Finders Keepers Summertown specialises in lettings and property management. They offer comprehensive landlord services including tenant finding, referencing, rent collection, and ongoing property maintenance.",
      },
      {
        question: "What are Finders Keepers Summertown's opening hours?",
        answer:
          "Finders Keepers Summertown is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 12:30. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "finders-keepers-east-oxford",
    name: "Finders Keepers",
    branch: "East Oxford",
    address: "106a Cowley Road",
    postcode: "OX4 1JE",
    phone: "01865 297999",
    website: "https://www.ffreslettings.co.uk",
    email: "cowleyroad@finderskeepers.co.uk",
    description:
      "Finders Keepers' East Oxford branch on Cowley Road covers one of the city's most vibrant and diverse rental markets. With strong demand from students, academics, and young professionals, their team manages a large portfolio across East Oxford, Cowley, Iffley, and the surrounding areas.",
    established: 1976,
    specialisms: ["Lettings", "Property Management"],
    areas: ["East Oxford", "Cowley", "Iffley", "Rose Hill", "Littlemore", "Grandpont"],
    propertyCount: 200,
    avgPrice: "£1,400/month",
    rating: 4.2,
    reviewCount: 278,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–12:30" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Finders Keepers' East Oxford branch on Cowley Road sits at the heart of one of the city's most vibrant and diverse rental markets. Managing around 200 properties with an average rent of £1,400 per month, the team has deep expertise in this fast-moving area where demand from students, academics, and young professionals remains consistently strong.\n\nThe branch covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Grandpont, offering the same thorough approach to tenant referencing and property management that has defined Finders Keepers since 1976. Their local knowledge of the OX4 postcode is second to none.\n\nWith 278 reviews and a 4.2-star rating, this is a branch that landlords and tenants return to, built on decades of consistent, reliable service in East Oxford's unique rental market.",
    whyChoose: [
      "Part of Oxford's best-known independent lettings agency, established 1976",
      "Rated 4.2/5 from 278 client reviews",
      "Around 200 managed properties across East Oxford",
      "Specialist knowledge of the OX4 postcode and Cowley Road corridor",
    ],
    faq: [
      {
        question: "What areas does Finders Keepers East Oxford cover?",
        answer:
          "Finders Keepers East Oxford covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Grandpont, with particular strength in the Cowley Road corridor and surrounding streets.",
      },
      {
        question: "What services does Finders Keepers East Oxford offer?",
        answer:
          "Finders Keepers East Oxford specialises in lettings and property management. They provide full landlord services including tenant finding, referencing, rent collection, and property maintenance across the East Oxford area.",
      },
      {
        question: "What are Finders Keepers East Oxford's opening hours?",
        answer:
          "Finders Keepers East Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 12:30. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "scott-fraser-summertown",
    name: "Scott Fraser",
    branch: "Summertown",
    address: "261 Banbury Road, Summertown",
    postcode: "OX2 7HU",
    phone: "01865 759500",
    website: "https://www.scottfraser.co.uk",
    email: "summertown@scottfraser.co.uk",
    description:
      "Scott Fraser is one of Oxford's leading independent estate and lettings agents, with their Summertown office on Banbury Road serving as their flagship branch. With over 25 years in the Oxford market, they handle residential sales and lettings across the city, with a strong reputation for quality service and genuine local expertise.",
    established: 1997,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Summertown", "North Oxford", "Wolvercote", "Cutteslowe", "Jericho", "Oxford City Centre"],
    propertyCount: 120,
    avgPrice: "£625,000",
    rating: 4.6,
    reviewCount: 218,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Scott Fraser has been one of Oxford's leading independent estate and lettings agents since 1997, and their Summertown flagship on Banbury Road is where that reputation was built. With around 120 properties on their books and an average price of £625,000, the team handles both sales and lettings across a broad swathe of the city.\n\nThe office covers Summertown, North Oxford, Wolvercote, Cutteslowe, Jericho, and Oxford City Centre, offering a level of service that larger corporate agents often struggle to match. Their local knowledge is deeply personal; many of the team have lived and worked in these neighbourhoods for years.\n\nWith a 4.6-star rating from 218 reviews, Scott Fraser Summertown is a consistent choice for sellers, buyers, landlords, and tenants who want genuine expertise without the corporate overhead.",
    whyChoose: [
      "Over 25 years as a leading Oxford independent, established 1997",
      "Rated 4.6/5 from 218 client reviews",
      "Full sales and lettings service across North Oxford and the city centre",
      "Personally invested local team with deep neighbourhood knowledge",
    ],
    faq: [
      {
        question: "What areas does Scott Fraser Summertown cover?",
        answer:
          "Scott Fraser Summertown covers Summertown, North Oxford, Wolvercote, Cutteslowe, Jericho, and Oxford City Centre, with strong knowledge of the residential streets north of the city centre.",
      },
      {
        question: "What services does Scott Fraser Summertown offer?",
        answer:
          "Scott Fraser Summertown provides residential sales, lettings, and property management. Their team handles everything from property valuations and marketing through to tenant management and ongoing maintenance.",
      },
      {
        question: "What are Scott Fraser Summertown's opening hours?",
        answer:
          "Scott Fraser Summertown is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "whitegates-oxford",
    name: "Whitegates",
    branch: "East Oxford",
    address: "94 Cowley Road",
    postcode: "OX4 1JE",
    phone: "01865 242526",
    website: "https://www.whitegates.co.uk/estate-agents/oxford",
    email: "oxford@whitegates.co.uk",
    description:
      "Whitegates East Oxford is a well-known high-street agent on Cowley Road, part of a national network with strong local roots. Their team handles sales and lettings across the vibrant east Oxford area, Cowley, Iffley, and beyond, with a focus on accessible, no-nonsense service for buyers, sellers, landlords, and tenants.",
    established: 1978,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["East Oxford", "Cowley", "Iffley", "Rose Hill", "Littlemore", "Headington"],
    propertyCount: 80,
    avgPrice: "£410,000",
    rating: 4.3,
    reviewCount: 165,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Whitegates East Oxford has been a familiar presence on Cowley Road since 1978, offering a no-nonsense approach to property sales and lettings. Part of a national network but with strong local roots, the branch handles around 80 properties with an average price of £410,000, making them one of the more accessible agents in the area.\n\nThe team covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Headington, working with a broad mix of clients from first-time buyers to growing families. Their straightforward service style and transparent fees have earned them a steady following in this price-sensitive part of the market.\n\nWith a 4.3-star rating from 165 reviews, Whitegates is a reliable option for anyone looking to buy, sell, or rent in the vibrant communities east of the city centre.",
    whyChoose: [
      "Established high-street presence on Cowley Road since 1978",
      "Rated 4.3/5 from 165 client reviews",
      "Accessible pricing with average listings around £410,000",
      "National network reach with a locally rooted, no-nonsense approach",
    ],
    faq: [
      {
        question: "What areas does Whitegates East Oxford cover?",
        answer:
          "Whitegates East Oxford covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Headington, with a focus on the residential areas east of the city centre.",
      },
      {
        question: "What services does Whitegates East Oxford offer?",
        answer:
          "Whitegates East Oxford provides residential sales, lettings, and property management. They offer a full service for buyers, sellers, landlords, and tenants at all price points.",
      },
      {
        question: "What are Whitegates East Oxford's opening hours?",
        answer:
          "Whitegates East Oxford is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "breckon-breckon-headington",
    name: "Breckon & Breckon",
    branch: "Headington",
    address: "118 London Road, Headington",
    postcode: "OX3 9AX",
    phone: "01865 764644",
    website: "https://www.breckon.co.uk",
    email: "headington@breckon.co.uk",
    description:
      "Breckon & Breckon is a long-established independent Oxford estate agent with their Headington office on London Road. Known for their straightforward approach and strong community ties, they handle sales and lettings across Headington, Marston, Old Marston, and the eastern Oxford suburbs.",
    established: 1967,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Headington", "Marston", "Old Marston", "East Oxford", "Cowley", "Littlemore"],
    propertyCount: 110,
    avgPrice: "£485,000",
    rating: 4.4,
    reviewCount: 256,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Breckon & Breckon has been a trusted name in Oxford property since 1967, and their Headington office on London Road is one of the area's longest-serving independent agents. With around 110 properties on their books and an average price of £485,000, the team handles sales and lettings across Headington, Marston, Old Marston, and the eastern suburbs.\n\nThe branch is known for its straightforward approach and genuine community ties. Many of the team live locally and bring first-hand knowledge of the schools, transport links, and neighbourhood character that matter to families and professionals choosing where to settle.\n\nWith a 4.4-star rating from 256 reviews and nearly six decades of experience, Breckon & Breckon Headington offers the kind of deep local expertise that only comes with time.",
    whyChoose: [
      "Independent Oxford agent with nearly 60 years of local experience",
      "Rated 4.4/5 from 256 client reviews",
      "Strong community presence across Headington and eastern Oxford",
      "Sales, lettings, property management, and new homes under one roof",
    ],
    faq: [
      {
        question: "What areas does Breckon & Breckon Headington cover?",
        answer:
          "Breckon & Breckon Headington covers Headington, Marston, Old Marston, East Oxford, Cowley, and Littlemore, with a strong focus on the residential areas surrounding London Road and Headington Hill.",
      },
      {
        question: "What services does Breckon & Breckon Headington offer?",
        answer:
          "Breckon & Breckon Headington provides residential sales, lettings, property management, and new homes services. Their team offers valuations, marketing, tenant referencing, and ongoing property maintenance.",
      },
      {
        question: "What are Breckon & Breckon Headington's opening hours?",
        answer:
          "Breckon & Breckon Headington is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "breckon-breckon-summertown",
    name: "Breckon & Breckon",
    branch: "Summertown",
    address: "264 Banbury Road, Summertown",
    postcode: "OX2 7DL",
    phone: "01865 552552",
    website: "https://www.breckon.co.uk",
    email: "summertown@breckon.co.uk",
    description:
      "Breckon & Breckon's Summertown branch on Banbury Road covers north Oxford and the city's most prestigious residential streets. With decades of local experience, their team handles sales and lettings for family homes, period properties, and new-build developments across the North Oxford area.",
    established: 1967,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Summertown", "North Oxford", "Wolvercote", "Cutteslowe", "Jericho"],
    propertyCount: 90,
    avgPrice: "£750,000",
    rating: 4.5,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Breckon & Breckon's Summertown branch on Banbury Road covers some of Oxford's most prestigious residential streets. Since 1967, the firm has built its reputation on honest advice and a genuine understanding of the local market, and this branch brings that approach to North Oxford's premium property landscape.\n\nWith around 90 properties on their books and an average price of £750,000, the team handles sales and lettings across Summertown, North Oxford, Wolvercote, Cutteslowe, and Jericho. Their strength lies in family homes, period properties, and new-build developments in the area.\n\nRated 4.5 stars from 198 reviews, Breckon & Breckon Summertown combines the independence and personal touch of a local firm with the experience of nearly six decades in the Oxford market.",
    whyChoose: [
      "Independent Oxford agency with nearly 60 years of market experience",
      "Rated 4.5/5 from 198 client reviews",
      "Specialists in North Oxford family homes and period properties",
      "Sales, lettings, property management, and new homes services",
    ],
    faq: [
      {
        question: "What areas does Breckon & Breckon Summertown cover?",
        answer:
          "Breckon & Breckon Summertown covers Summertown, North Oxford, Wolvercote, Cutteslowe, and Jericho, with a particular focus on the residential streets off Banbury Road and Woodstock Road.",
      },
      {
        question: "What services does Breckon & Breckon Summertown offer?",
        answer:
          "Breckon & Breckon Summertown provides residential sales, lettings, property management, and new homes services. They handle everything from valuations and marketing to tenant management and maintenance.",
      },
      {
        question: "What are Breckon & Breckon Summertown's opening hours?",
        answer:
          "Breckon & Breckon Summertown is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "thomas-merrifield-oxford",
    name: "Thomas Merrifield",
    branch: "Oxford",
    address: "165 Cowley Road",
    postcode: "OX4 1HS",
    phone: "01865 722077",
    website: "https://www.thomasmerrifield.co.uk",
    email: "oxford@thomasmerrifield.co.uk",
    description:
      "Thomas Merrifield is an award-winning independent estate agent with multiple offices across Oxfordshire. Their Oxford branch on Cowley Road handles sales and lettings across the city's eastern and southern areas. Winners of numerous industry awards, they bring a fresh, energetic approach to the Oxford property market.",
    established: 2005,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["East Oxford", "Cowley", "Iffley", "Rose Hill", "Littlemore", "Grandpont"],
    propertyCount: 85,
    avgPrice: "£430,000",
    rating: 4.6,
    reviewCount: 234,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Thomas Merrifield has brought a fresh, award-winning approach to Oxford property since 2005. Their Cowley Road branch handles sales and lettings across the city's eastern and southern areas, with around 85 properties on their books and an average price of £430,000. Multiple industry awards speak to the quality and energy of their team.\n\nCovering East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Grandpont, Thomas Merrifield appeals to a broad client base, from first-time buyers to families seeking more space. Their marketing is consistently strong, with professional photography and proactive buyer matching as standard.\n\nWith a 4.6-star rating from 234 reviews, Thomas Merrifield Oxford has earned a reputation for punching well above its weight in a competitive market. Their youthful energy is backed by genuine expertise.",
    whyChoose: [
      "Award-winning independent agency with a strong Oxfordshire presence",
      "Rated 4.6/5 from 234 client reviews",
      "Proactive marketing with professional photography as standard",
      "Broad coverage across East and South Oxford at accessible price points",
    ],
    faq: [
      {
        question: "What areas does Thomas Merrifield Oxford cover?",
        answer:
          "Thomas Merrifield Oxford covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Grandpont, with wider reach across Oxfordshire through their multi-branch network.",
      },
      {
        question: "What services does Thomas Merrifield Oxford offer?",
        answer:
          "Thomas Merrifield Oxford provides residential sales, lettings, property management, and new homes services. They also offer free property valuations and comprehensive marketing packages.",
      },
      {
        question: "What are Thomas Merrifield Oxford's opening hours?",
        answer:
          "Thomas Merrifield Oxford is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "penny-sinclair-north-oxford",
    name: "Penny & Sinclair",
    branch: "North Oxford",
    address: "280 Banbury Road, Summertown",
    postcode: "OX2 7ED",
    phone: "01865 318011",
    website: "https://www.pennyandsinclair.co.uk",
    email: "summertown@pennyandsinclair.co.uk",
    description:
      "Penny & Sinclair is a premium independent estate agent specialising in North Oxford's finest residential streets. From their Banbury Road office, they handle sales and lettings of distinguished properties including Victorian villas, Edwardian houses, and period conversions. Their bespoke approach and discreet service attract discerning buyers and sellers.",
    established: 2008,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["North Oxford", "Summertown", "Wolvercote", "Jericho", "Oxford City Centre"],
    propertyCount: 45,
    avgPrice: "£1,150,000",
    rating: 4.8,
    reviewCount: 86,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Penny & Sinclair is a premium independent estate agent that has carved out a distinctive position in North Oxford's property market since 2008. From their Banbury Road office, the team handles sales and lettings of some of the city's finest homes, with an average listing price of £1,150,000 reflecting their focus on the upper end of the market.\n\nSpecialising in Victorian villas, Edwardian houses, and period conversions across North Oxford, Summertown, Wolvercote, Jericho, and the city centre, their bespoke approach attracts discerning buyers and sellers who value quality over volume. With 45 carefully selected properties on their books, every instruction receives personal attention.\n\nRated 4.8 stars from 86 reviews, Penny & Sinclair North Oxford delivers the kind of discreet, considered service that the area's most distinguished properties deserve.",
    whyChoose: [
      "Rated 4.8/5 from 86 client reviews, the highest in the Oxford directory",
      "Specialists in Victorian villas and Edwardian homes in North Oxford",
      "Bespoke, quality-focused approach with an average listing price of £1,150,000",
      "Personal service with a curated portfolio of distinguished properties",
    ],
    faq: [
      {
        question: "What areas does Penny & Sinclair North Oxford cover?",
        answer:
          "Penny & Sinclair North Oxford covers North Oxford, Summertown, Wolvercote, Jericho, and Oxford City Centre, with a particular strength in the premium residential streets between Banbury Road and Woodstock Road.",
      },
      {
        question: "What services does Penny & Sinclair North Oxford offer?",
        answer:
          "Penny & Sinclair North Oxford provides residential sales and lettings, with a focus on premium and period properties. Their service includes professional photography, bespoke marketing, and discreet buyer matching.",
      },
      {
        question: "What are Penny & Sinclair North Oxford's opening hours?",
        answer:
          "Penny & Sinclair North Oxford is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "penny-sinclair-headington",
    name: "Penny & Sinclair",
    branch: "Headington",
    address: "128 London Road, Headington",
    postcode: "OX3 9ED",
    phone: "01865 318013",
    website: "https://www.pennyandsinclair.co.uk",
    email: "headington@pennyandsinclair.co.uk",
    description:
      "Penny & Sinclair's Headington branch extends their premium service to east Oxford. Positioned on London Road, they cover Headington, Old Marston, and the surrounding areas, handling sales and lettings of distinctive homes. Their attention to detail and quality marketing sets them apart in the Headington market.",
    established: 2008,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Headington", "Old Marston", "Marston", "East Oxford", "Cowley"],
    propertyCount: 35,
    avgPrice: "£685,000",
    rating: 4.7,
    reviewCount: 68,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Penny & Sinclair's Headington branch extends their premium, quality-focused service to eastern Oxford. Positioned on London Road, the team covers Headington, Old Marston, Marston, East Oxford, and Cowley, handling sales and lettings of distinctive homes with the same attention to detail that defines their North Oxford operation.\n\nWith 35 carefully chosen properties on their books and an average listing price of £685,000, this branch operates at the upper end of the Headington market. Their marketing is a cut above, with professional photography, detailed property descriptions, and targeted buyer outreach.\n\nRated 4.7 stars from 68 reviews, Penny & Sinclair Headington is the natural choice for homeowners who want their property presented with care, precision, and a genuine understanding of what makes Headington special.",
    whyChoose: [
      "Rated 4.7/5 from 68 client reviews",
      "Premium marketing with professional photography and targeted outreach",
      "Curated portfolio of distinctive Headington and East Oxford homes",
      "Part of Penny & Sinclair, known for quality-focused independent service",
    ],
    faq: [
      {
        question: "What areas does Penny & Sinclair Headington cover?",
        answer:
          "Penny & Sinclair Headington covers Headington, Old Marston, Marston, East Oxford, and Cowley, with a particular focus on the residential streets around Headington Hill and London Road.",
      },
      {
        question: "What services does Penny & Sinclair Headington offer?",
        answer:
          "Penny & Sinclair Headington provides residential sales and lettings. Their service includes professional property marketing, buyer matching, and attentive client management throughout the transaction.",
      },
      {
        question: "What are Penny & Sinclair Headington's opening hours?",
        answer:
          "Penny & Sinclair Headington is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "chancellors-headington",
    name: "Chancellors",
    branch: "Headington",
    address: "112 London Road, Headington",
    postcode: "OX3 9AX",
    phone: "01865 759222",
    website: "https://www.chancellors.co.uk/estate-agents/headington",
    email: "headington@chancellors.co.uk",
    description:
      "Chancellors Headington is part of a well-known regional chain with strong roots in Oxfordshire. Their London Road office handles sales and lettings across Headington, Marston, and eastern Oxford, with a high-volume, reliable service. They cover everything from student rentals to substantial family homes in the Headington Hill area.",
    established: 1990,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Headington", "Marston", "Old Marston", "East Oxford", "Cowley", "Littlemore"],
    propertyCount: 120,
    avgPrice: "£440,000",
    rating: 4.2,
    reviewCount: 245,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Chancellors has been a recognisable name across Oxfordshire since 1990, and their Headington office on London Road is one of their busiest branches. Handling around 120 properties with an average price of £440,000, the team provides a high-volume, reliable service covering sales, lettings, and property management.\n\nThe branch covers Headington, Marston, Old Marston, East Oxford, Cowley, and Littlemore, working with everything from student rental properties to substantial family homes near Headington Hill. Their regional network gives them access to a large buyer and tenant database, which translates into quicker lettings and stronger sale prices.\n\nWith a 4.2-star rating from 245 reviews, Chancellors Headington is a dependable choice for clients who value efficiency, reach, and a team that understands the eastern Oxford market inside out.",
    whyChoose: [
      "Well-known regional brand with over 30 years in Oxfordshire",
      "Rated 4.2/5 from 245 client reviews",
      "High-volume service with around 120 properties on their books",
      "Access to a large regional buyer and tenant database",
    ],
    faq: [
      {
        question: "What areas does Chancellors Headington cover?",
        answer:
          "Chancellors Headington covers Headington, Marston, Old Marston, East Oxford, Cowley, and Littlemore, with a strong understanding of the residential areas along London Road and Headington Hill.",
      },
      {
        question: "What services does Chancellors Headington offer?",
        answer:
          "Chancellors Headington provides residential sales, lettings, and property management. Their team handles property valuations, marketing, tenant referencing, and ongoing maintenance across the eastern Oxford area.",
      },
      {
        question: "What are Chancellors Headington's opening hours?",
        answer:
          "Chancellors Headington is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "chancellors-summertown",
    name: "Chancellors",
    branch: "Summertown",
    address: "272 Banbury Road, Summertown",
    postcode: "OX2 7DL",
    phone: "01865 553456",
    website: "https://www.chancellors.co.uk/estate-agents/summertown",
    email: "summertown@chancellors.co.uk",
    description:
      "Chancellors' Summertown branch is one of their busiest Oxford offices, handling sales and lettings across North Oxford's premium residential market. Their Banbury Road location puts them at the heart of the Summertown high street, with strong knowledge of the area's family homes and period properties.",
    established: 1990,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Summertown", "North Oxford", "Wolvercote", "Cutteslowe", "Jericho"],
    propertyCount: 100,
    avgPrice: "£680,000",
    rating: 4.3,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Chancellors' Summertown branch is one of their flagship Oxford offices, positioned on Banbury Road at the heart of the high street. With around 100 properties and an average price of £680,000, the team handles sales and lettings across North Oxford's premium residential market, where demand consistently outstrips supply.\n\nThe branch covers Summertown, North Oxford, Wolvercote, Cutteslowe, and Jericho, with strong knowledge of the family homes and period properties that characterise these sought-after neighbourhoods. Their regional network and new homes division add further reach for buyers and sellers.\n\nRated 4.3 stars from 198 reviews, Chancellors Summertown combines the resources of a well-established regional chain with a team that knows the North Oxford market intimately. Their Banbury Road presence makes them one of the most visible agents in the area.",
    whyChoose: [
      "Flagship Banbury Road location in the heart of Summertown",
      "Rated 4.3/5 from 198 client reviews",
      "Strong coverage of North Oxford's premium family home market",
      "Regional network with new homes, sales, lettings, and property management",
    ],
    faq: [
      {
        question: "What areas does Chancellors Summertown cover?",
        answer:
          "Chancellors Summertown covers Summertown, North Oxford, Wolvercote, Cutteslowe, and Jericho, with a particular focus on the residential streets between Banbury Road and Woodstock Road.",
      },
      {
        question: "What services does Chancellors Summertown offer?",
        answer:
          "Chancellors Summertown provides residential sales, lettings, property management, and new homes services. They also offer free property valuations and access to their regional buyer database.",
      },
      {
        question: "What are Chancellors Summertown's opening hours?",
        answer:
          "Chancellors Summertown is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "jackson-stops-oxford",
    name: "Jackson-Stops",
    branch: "Oxford",
    address: "15 Banbury Road",
    postcode: "OX2 6JX",
    phone: "01865 311100",
    website: "https://www.jackson-stops.co.uk/branches/oxford",
    email: "oxford@jackson-stops.co.uk",
    description:
      "Jackson-Stops' Oxford office on Banbury Road serves the premium end of the city's property market. With a heritage dating to 1910, they specialise in fine period houses, country homes, and distinctive properties across Oxford and the surrounding Oxfordshire villages. Their team combines national reach with genuine local knowledge.",
    established: 1910,
    specialisms: ["Residential Sales", "Lettings", "Land & Development"],
    areas: ["North Oxford", "Summertown", "Jericho", "Wolvercote", "Oxford City Centre"],
    propertyCount: 40,
    avgPrice: "£925,000",
    rating: 4.6,
    reviewCount: 72,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Jackson-Stops has been a trusted name in fine property since 1910, and their Oxford office on Banbury Road brings over a century of heritage to the city's premium market. With around 40 carefully selected properties and an average listing price of £925,000, this is an office that prioritises quality over volume.\n\nThe team covers North Oxford, Summertown, Jericho, Wolvercote, and the city centre, with additional expertise in country homes and estates across the wider Oxfordshire countryside. Their particular strength lies in period houses and distinctive properties that demand considered, expert marketing.\n\nWith a 4.6-star rating from 72 reviews, Jackson-Stops Oxford is the choice for those who want their property handled by a team that genuinely understands heritage architecture and the nuances of the premium Oxford market.",
    whyChoose: [
      "Over a century of heritage in fine property since 1910",
      "Rated 4.6/5 from 72 client reviews",
      "Specialists in period houses, country homes, and distinctive Oxford properties",
      "Curated portfolio with an average listing price of £925,000",
    ],
    faq: [
      {
        question: "What areas does Jackson-Stops Oxford cover?",
        answer:
          "Jackson-Stops Oxford covers North Oxford, Summertown, Jericho, Wolvercote, and Oxford City Centre, with wider expertise in country properties and estates across the Oxfordshire villages.",
      },
      {
        question: "What services does Jackson-Stops Oxford offer?",
        answer:
          "Jackson-Stops Oxford provides residential sales, lettings, and land and development services. They specialise in fine period houses, country homes, and distinctive properties across Oxford and Oxfordshire.",
      },
      {
        question: "What are Jackson-Stops Oxford's opening hours?",
        answer:
          "Jackson-Stops Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hamptons-oxford",
    name: "Hamptons",
    branch: "Oxford",
    address: "254 Banbury Road, Summertown",
    postcode: "OX2 7DE",
    phone: "01865 302100",
    website: "https://www.hamptons.co.uk/estate-agents/oxford",
    email: "oxford@hamptons.co.uk",
    description:
      "Hamptons Oxford occupies a prominent Banbury Road position in Summertown, handling premium residential sales and lettings across the city. Part of the Connells Group, they combine local expertise with a national buyer network, with particular strength in North Oxford's Victorian and Edwardian properties.",
    established: 1869,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Relocation"],
    areas: ["Summertown", "North Oxford", "Jericho", "Wolvercote", "Oxford City Centre"],
    propertyCount: 60,
    avgPrice: "£785,000",
    rating: 4.4,
    reviewCount: 108,
    hours: [
      { days: "Mon–Fri", time: "08:45–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Hamptons has been in the property business since 1869, and their Oxford office on Banbury Road in Summertown brings that considerable heritage to one of the city's most competitive markets. With around 60 properties and an average listing price of £785,000, the team operates at the premium end of Oxford residential sales and lettings.\n\nAs part of the Connells Group, Hamptons Oxford combines local expertise with a national buyer network, giving sellers access to a significantly wider pool of qualified purchasers. Their particular strength lies in North Oxford's Victorian and Edwardian properties, where period charm meets strong capital growth.\n\nRated 4.4 stars from 108 reviews, Hamptons Oxford also offers new homes and relocation services, making them a versatile choice for those navigating the Oxford property market at any stage.",
    whyChoose: [
      "Over 150 years of property heritage since 1869",
      "Rated 4.4/5 from 108 client reviews",
      "National buyer network through the Connells Group",
      "Specialists in North Oxford's Victorian and Edwardian properties",
    ],
    faq: [
      {
        question: "What areas does Hamptons Oxford cover?",
        answer:
          "Hamptons Oxford covers Summertown, North Oxford, Jericho, Wolvercote, and Oxford City Centre, with a strong focus on the premium residential streets in the northern part of the city.",
      },
      {
        question: "What services does Hamptons Oxford offer?",
        answer:
          "Hamptons Oxford provides residential sales, lettings, new homes, and relocation services. Their team offers valuations, marketing, buyer matching, and support throughout the buying and selling process.",
      },
      {
        question: "What are Hamptons Oxford's opening hours?",
        answer:
          "Hamptons Oxford is open Monday to Friday from 08:45 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "james-c-penny-oxford",
    name: "James C Penny",
    branch: "Jericho",
    address: "51 Walton Street, Jericho",
    postcode: "OX2 6AE",
    phone: "01865 515151",
    website: "https://www.jamescpenny.co.uk",
    email: "info@jamescpenny.co.uk",
    description:
      "James C Penny is a well-established independent lettings specialist based in the heart of Jericho on Walton Street. With decades of experience in the Oxford rental market, they manage a large portfolio of properties across the city, with particular expertise in Jericho, North Oxford, and the central Oxford area.",
    established: 1985,
    specialisms: ["Lettings", "Property Management"],
    areas: ["Jericho", "North Oxford", "Summertown", "Oxford City Centre", "Osney", "Botley"],
    propertyCount: 190,
    avgPrice: "£1,500/month",
    rating: 4.3,
    reviewCount: 267,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–12:30" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "James C Penny has been a cornerstone of the Oxford lettings market since 1985, operating from the heart of Jericho on Walton Street. With around 190 managed properties and an average rent of £1,500 per month, they are one of the city's largest and most experienced independent lettings specialists.\n\nThe team covers Jericho, North Oxford, Summertown, Oxford City Centre, Osney, and Botley, with particular expertise in properties suited to academics, professionals, and families. Four decades of local knowledge means they understand the rhythms of the Oxford rental market better than most, from term-time demand to the nuances of each neighbourhood.\n\nWith a 4.3-star rating from 267 reviews, James C Penny is a trusted choice for landlords seeking proactive management and tenants looking for quality homes in some of Oxford's most desirable areas.",
    whyChoose: [
      "Four decades of Oxford lettings experience since 1985",
      "Rated 4.3/5 from 267 client reviews",
      "Around 190 managed properties across central and North Oxford",
      "Jericho-based specialists with deep knowledge of the academic rental market",
    ],
    faq: [
      {
        question: "What areas does James C Penny cover?",
        answer:
          "James C Penny covers Jericho, North Oxford, Summertown, Oxford City Centre, Osney, and Botley, with a strong focus on properties in the central and northern parts of the city.",
      },
      {
        question: "What services does James C Penny offer?",
        answer:
          "James C Penny specialises in lettings and property management. They provide comprehensive landlord services including tenant finding, referencing, rent collection, and ongoing property maintenance.",
      },
      {
        question: "What are James C Penny's opening hours?",
        answer:
          "James C Penny is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 12:30. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "north-oxford-property-service",
    name: "North Oxford Property Service",
    branch: "North Oxford",
    address: "286 Banbury Road, Summertown",
    postcode: "OX2 7ED",
    phone: "01865 311444",
    website: "https://www.northoxfordpropertyservice.co.uk",
    email: "info@northoxfordpropertyservice.co.uk",
    description:
      "North Oxford Property Service is a specialist lettings and property management agency focused exclusively on North Oxford. From their Banbury Road office, they manage a curated portfolio of quality rental properties, catering primarily to visiting academics, professionals, and families seeking homes in Oxford's most sought-after streets.",
    established: 1998,
    specialisms: ["Lettings", "Property Management"],
    areas: ["North Oxford", "Summertown", "Wolvercote", "Cutteslowe", "Jericho"],
    propertyCount: 120,
    avgPrice: "£1,850/month",
    rating: 4.5,
    reviewCount: 134,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:00" },
      { days: "Sat", time: "Closed" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "North Oxford Property Service is a specialist lettings and property management agency focused exclusively on the most sought-after part of the city. From their Banbury Road office, the team manages around 120 properties with an average rent of £1,850 per month, catering to a discerning tenant base of visiting academics, senior professionals, and families.\n\nSince 1998, the agency has built its reputation on quality over quantity. Covering North Oxford, Summertown, Wolvercote, Cutteslowe, and Jericho, they curate a portfolio of well-maintained homes in the residential streets that make this area so desirable. Their understanding of the academic calendar and University-linked demand is second to none.\n\nWith a 4.5-star rating from 134 reviews, North Oxford Property Service is the go-to agency for landlords who want their properties managed with care and tenants who expect a higher standard.",
    whyChoose: [
      "Exclusively focused on North Oxford's premium rental market",
      "Rated 4.5/5 from 134 client reviews",
      "Specialist knowledge of the academic and University-linked tenant market",
      "Curated portfolio of quality homes with an average rent of £1,850 per month",
    ],
    faq: [
      {
        question: "What areas does North Oxford Property Service cover?",
        answer:
          "North Oxford Property Service covers North Oxford, Summertown, Wolvercote, Cutteslowe, and Jericho, with a tight focus on the premium residential streets in the northern part of the city.",
      },
      {
        question: "What services does North Oxford Property Service offer?",
        answer:
          "North Oxford Property Service specialises in lettings and property management. They offer full landlord services including tenant finding, referencing, rent collection, inspections, and maintenance management.",
      },
      {
        question: "What are North Oxford Property Service's opening hours?",
        answer:
          "North Oxford Property Service is open Monday to Friday from 09:00 to 17:00. They are closed on Saturdays and Sundays.",
      },
    ],
  },
  {
    slug: "connells-headington",
    name: "Connells",
    branch: "Headington",
    address: "104 London Road, Headington",
    postcode: "OX3 9AX",
    phone: "01865 762626",
    website: "https://www.connells.co.uk/estate-agents/headington",
    email: "headington@connells.co.uk",
    description:
      "Connells Headington is part of the UK's largest estate agency group, with their London Road office serving as a busy hub for residential sales and lettings. Their team covers Headington, Marston, and the wider eastern Oxford area, offering a high-volume, dependable service backed by a national buyer database.",
    established: 1936,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Headington", "Marston", "Old Marston", "Cowley", "Littlemore", "Rose Hill"],
    propertyCount: 130,
    avgPrice: "£395,000",
    rating: 4.1,
    reviewCount: 267,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Connells has been in the estate agency business since 1936, and their Headington branch on London Road is one of the busiest offices in eastern Oxford. As part of the UK's largest estate agency group, they bring considerable resources to the local market, handling around 130 properties with an average price of £395,000.\n\nThe branch covers Headington, Marston, Old Marston, Cowley, Littlemore, and Rose Hill, offering a high-volume service that spans sales, lettings, property management, and new homes. Their national buyer database gives sellers access to a wide pool of active purchasers, while their local team provides day-to-day knowledge of the area.\n\nRated 4.1 stars from 267 reviews, Connells Headington is a solid, dependable option for anyone looking to buy, sell, or rent in the affordable end of the Oxford market.",
    whyChoose: [
      "Part of the UK's largest estate agency group, established 1936",
      "Rated 4.1/5 from 267 client reviews",
      "Around 130 properties with access to a national buyer database",
      "Full service covering sales, lettings, property management, and new homes",
    ],
    faq: [
      {
        question: "What areas does Connells Headington cover?",
        answer:
          "Connells Headington covers Headington, Marston, Old Marston, Cowley, Littlemore, and Rose Hill, with a focus on the residential areas in eastern and south-eastern Oxford.",
      },
      {
        question: "What services does Connells Headington offer?",
        answer:
          "Connells Headington provides residential sales, lettings, property management, and new homes services. They also offer mortgage advice and conveyancing through their in-house teams.",
      },
      {
        question: "What are Connells Headington's opening hours?",
        answer:
          "Connells Headington is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "martin-co-oxford",
    name: "Martin & Co",
    branch: "Oxford",
    address: "16 Turl Street",
    postcode: "OX1 3DH",
    phone: "01865 501100",
    website: "https://www.martinco.com/branch/oxford",
    email: "oxford@martinco.com",
    description:
      "Martin & Co Oxford is a city-centre lettings and estate agency on Turl Street, near the Covered Market. As part of a national franchise network, they handle sales and lettings across Oxford, with a strong lettings portfolio catering to students, professionals, and families. Their central location makes them easily accessible for city-centre viewings.",
    established: 1986,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Oxford City Centre", "Jericho", "Osney", "Grandpont", "East Oxford", "Abingdon Road"],
    propertyCount: 85,
    avgPrice: "£420,000",
    rating: 4.2,
    reviewCount: 176,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Martin & Co Oxford occupies a prime city-centre location on Turl Street, near the Covered Market. As part of a national franchise network, the branch handles around 85 properties with an average price of £420,000, covering sales and lettings across the central and eastern parts of the city.\n\nThe team serves Oxford City Centre, Jericho, Osney, Grandpont, East Oxford, and Abingdon Road, with a particularly strong lettings portfolio catering to students, professionals, and families. Their central location makes them easily accessible for viewings and drop-in valuations, which sets them apart from agents based further out.\n\nWith a 4.2-star rating from 176 reviews, Martin & Co Oxford is a practical choice for landlords and tenants who want a well-connected, city-centre agent with the backing of a national brand.",
    whyChoose: [
      "Prime city-centre location on Turl Street for easy access",
      "Rated 4.2/5 from 176 client reviews",
      "Strong lettings portfolio across central and East Oxford",
      "National franchise network providing broad buyer and tenant reach",
    ],
    faq: [
      {
        question: "What areas does Martin & Co Oxford cover?",
        answer:
          "Martin & Co Oxford covers Oxford City Centre, Jericho, Osney, Grandpont, East Oxford, and Abingdon Road, with a strong presence in the central areas of the city.",
      },
      {
        question: "What services does Martin & Co Oxford offer?",
        answer:
          "Martin & Co Oxford provides residential sales, lettings, and property management. Their city-centre office offers convenient access for valuations, viewings, and property management enquiries.",
      },
      {
        question: "What are Martin & Co Oxford's opening hours?",
        answer:
          "Martin & Co Oxford is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "allen-harris-oxford",
    name: "Allen & Harris",
    branch: "Oxford",
    address: "120 London Road, Headington",
    postcode: "OX3 9ED",
    phone: "01865 769611",
    website: "https://www.allenandharris.co.uk/estate-agents/oxford",
    email: "oxford@allenandharris.co.uk",
    description:
      "Allen & Harris Oxford is part of the Sequence national network, offering residential sales and lettings from their London Road office in Headington. With access to a vast buyer database and solid local experience, they provide a reliable service covering properties from first-time buys to family homes across Oxford.",
    established: 1946,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Headington", "Marston", "Old Marston", "Cowley", "East Oxford", "Littlemore"],
    propertyCount: 100,
    avgPrice: "£435,000",
    rating: 4.0,
    reviewCount: 213,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Allen & Harris has been part of the Oxford property landscape since 1946, with their London Road office in Headington serving as a reliable option for residential sales and lettings. Part of the Sequence national network, they handle around 100 properties with an average price of £435,000, offering accessible service across the eastern Oxford market.\n\nThe branch covers Headington, Marston, Old Marston, Cowley, East Oxford, and Littlemore, working with a broad range of clients from first-time buyers to downsizers. Their national buyer database and solid local experience combine to provide a dependable, no-frills service.\n\nWith a 4.0-star rating from 213 reviews and nearly eight decades of history, Allen & Harris Oxford is a straightforward choice for those looking to buy, sell, or rent at accessible price points in the eastern part of the city.",
    whyChoose: [
      "Nearly 80 years of estate agency experience since 1946",
      "Rated 4.0/5 from 213 client reviews",
      "Access to a national buyer database through the Sequence network",
      "Accessible pricing with average listings around £435,000",
    ],
    faq: [
      {
        question: "What areas does Allen & Harris Oxford cover?",
        answer:
          "Allen & Harris Oxford covers Headington, Marston, Old Marston, Cowley, East Oxford, and Littlemore, with a focus on the residential areas in eastern Oxford.",
      },
      {
        question: "What services does Allen & Harris Oxford offer?",
        answer:
          "Allen & Harris Oxford provides residential sales, lettings, property management, and new homes services. They also offer access to mortgage advice and conveyancing through their wider network.",
      },
      {
        question: "What are Allen & Harris Oxford's opening hours?",
        answer:
          "Allen & Harris Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 14:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "strutt-parker-oxford",
    name: "Strutt & Parker",
    branch: "Oxford",
    address: "260 Banbury Road, Summertown",
    postcode: "OX2 7DL",
    phone: "01865 366600",
    website: "https://www.struttandparker.com/offices/oxford",
    email: "oxford@struttandparker.com",
    description:
      "Strutt & Parker's Oxford office on Banbury Road handles premium residential sales and country property across the city and wider Oxfordshire. Part of BNP Paribas Real Estate, they bring over 130 years of heritage and expertise in fine homes, farms, and estates to the Oxford market.",
    established: 1885,
    specialisms: ["Residential Sales", "Lettings", "Land & Development"],
    areas: ["North Oxford", "Summertown", "Wolvercote", "Jericho", "Oxford City Centre"],
    propertyCount: 35,
    avgPrice: "£1,100,000",
    rating: 4.6,
    reviewCount: 58,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Strutt & Parker brings over 130 years of property heritage to their Oxford office on Banbury Road in Summertown. As part of BNP Paribas Real Estate, the team handles premium residential sales and country property across the city and wider Oxfordshire, with around 35 properties and an average listing price of £1,100,000.\n\nThe branch covers North Oxford, Summertown, Wolvercote, Jericho, and the city centre, with additional expertise in farms, estates, and country houses across the surrounding countryside. Their particular strength lies in fine period homes and substantial properties that require expert handling and a sophisticated approach to marketing.\n\nRated 4.6 stars from 58 reviews, Strutt & Parker Oxford is the natural choice for sellers of distinguished properties who value heritage, discretion, and a team that understands the premium Oxford and country house market.",
    whyChoose: [
      "Over 130 years of heritage in fine property and country estates",
      "Rated 4.6/5 from 58 client reviews",
      "Part of BNP Paribas Real Estate with global reach",
      "Specialists in premium Oxford homes with an average listing price of £1,100,000",
    ],
    faq: [
      {
        question: "What areas does Strutt & Parker Oxford cover?",
        answer:
          "Strutt & Parker Oxford covers North Oxford, Summertown, Wolvercote, Jericho, and Oxford City Centre, with wider expertise in country property, farms, and estates across the Oxfordshire countryside.",
      },
      {
        question: "What services does Strutt & Parker Oxford offer?",
        answer:
          "Strutt & Parker Oxford provides residential sales, lettings, and land and development services. They specialise in fine homes, country houses, farms, and estates across Oxford and Oxfordshire.",
      },
      {
        question: "What are Strutt & Parker Oxford's opening hours?",
        answer:
          "Strutt & Parker Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "carter-jonas-oxford",
    name: "Carter Jonas",
    branch: "Oxford",
    address: "2 St Giles'",
    postcode: "OX1 3JX",
    phone: "01865 404140",
    website: "https://www.carterjonas.co.uk/offices/oxford",
    email: "oxford@carterjonas.co.uk",
    description:
      "Carter Jonas Oxford occupies a distinguished St Giles' address, providing residential sales and lettings across the city and Oxfordshire countryside. Part of a national practice with a strong country house heritage, their Oxford team excels at advising on period homes, new developments, and rural property in the surrounding villages.",
    established: 1855,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development"],
    areas: ["Oxford City Centre", "North Oxford", "Jericho", "Summertown", "Wolvercote", "Botley"],
    propertyCount: 50,
    avgPrice: "£825,000",
    rating: 4.5,
    reviewCount: 84,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–12:30" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Carter Jonas has been in the property business since 1855, and their Oxford office on St Giles' occupies one of the city's most distinguished addresses. With around 50 properties and an average listing price of £825,000, the team handles residential sales and lettings across the city and the Oxfordshire countryside.\n\nThe branch covers Oxford City Centre, North Oxford, Jericho, Summertown, Wolvercote, and Botley, with a strong country house heritage that extends into the surrounding villages. Their team excels at advising on period homes, new developments, and rural property, combining national reach with genuine local insight.\n\nRated 4.5 stars from 84 reviews, Carter Jonas Oxford is a refined choice for sellers and buyers who appreciate the combination of a prestigious address, a long heritage, and a team that knows both the city and its rural surroundings.",
    whyChoose: [
      "Distinguished St Giles' address with heritage dating to 1855",
      "Rated 4.5/5 from 84 client reviews",
      "Strong country house and rural property expertise across Oxfordshire",
      "Full service: sales, lettings, new homes, and land and development",
    ],
    faq: [
      {
        question: "What areas does Carter Jonas Oxford cover?",
        answer:
          "Carter Jonas Oxford covers Oxford City Centre, North Oxford, Jericho, Summertown, Wolvercote, and Botley, with additional expertise in country property across the wider Oxfordshire villages.",
      },
      {
        question: "What services does Carter Jonas Oxford offer?",
        answer:
          "Carter Jonas Oxford provides residential sales, lettings, new homes, and land and development services. They specialise in period homes, new developments, and rural property across Oxford and Oxfordshire.",
      },
      {
        question: "What are Carter Jonas Oxford's opening hours?",
        answer:
          "Carter Jonas Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 12:30. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "winkworth-oxford",
    name: "Winkworth",
    branch: "Oxford",
    address: "275 Banbury Road, Summertown",
    postcode: "OX2 7JF",
    phone: "01865 308252",
    website: "https://www.winkworth.co.uk/estate-agents/oxford",
    email: "oxford@winkworth.co.uk",
    description:
      "Winkworth Oxford is a franchise of the national Winkworth brand, operating from Banbury Road in Summertown. Their team brings local expertise and a personal touch to residential sales and lettings across Oxford, with particular strength in North Oxford's Victorian and Edwardian streets.",
    established: 1835,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Summertown", "North Oxford", "Jericho", "Wolvercote", "Cutteslowe", "Oxford City Centre"],
    propertyCount: 50,
    avgPrice: "£695,000",
    rating: 4.4,
    reviewCount: 92,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Winkworth Oxford is a franchise of one of the UK's oldest property brands, operating from Banbury Road in Summertown since the brand's founding in 1835. The team brings a personal, locally invested approach to residential sales and lettings, with around 50 properties and an average price of £695,000.\n\nCovering Summertown, North Oxford, Jericho, Wolvercote, Cutteslowe, and Oxford City Centre, Winkworth has particular strength in the Victorian and Edwardian streets that define the character of North Oxford. Their franchise model means the office is owned and run by people who live and work in the area, not managed remotely.\n\nWith a 4.4-star rating from 92 reviews, Winkworth Oxford offers the credibility of a well-known national brand combined with the personal attention and local commitment of an independent operator.",
    whyChoose: [
      "Part of one of the UK's oldest property brands, established 1835",
      "Rated 4.4/5 from 92 client reviews",
      "Locally owned franchise with personal investment in the community",
      "Specialists in North Oxford's Victorian and Edwardian properties",
    ],
    faq: [
      {
        question: "What areas does Winkworth Oxford cover?",
        answer:
          "Winkworth Oxford covers Summertown, North Oxford, Jericho, Wolvercote, Cutteslowe, and Oxford City Centre, with a particular focus on the premium residential streets in the northern part of the city.",
      },
      {
        question: "What services does Winkworth Oxford offer?",
        answer:
          "Winkworth Oxford provides residential sales and lettings. Their locally owned franchise team offers property valuations, marketing, buyer matching, and support throughout the buying and selling process.",
      },
      {
        question: "What are Winkworth Oxford's opening hours?",
        answer:
          "Winkworth Oxford is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "oxford-hawk-lettings",
    name: "Oxford Hawk",
    branch: "Oxford",
    address: "37 St Clements Street",
    postcode: "OX4 1AB",
    phone: "01865 302030",
    website: "https://www.oxfordhawk.co.uk",
    email: "info@oxfordhawk.co.uk",
    description:
      "Oxford Hawk is an independent lettings and property management specialist based on St Clements. With a focus on quality rental properties, they manage a strong portfolio across East Oxford, the city centre, and the surrounding areas. Their hands-on approach and local knowledge make them a trusted choice for landlords and tenants.",
    established: 2011,
    specialisms: ["Lettings", "Property Management"],
    areas: ["East Oxford", "Oxford City Centre", "Cowley", "Iffley", "Headington", "Grandpont"],
    propertyCount: 110,
    avgPrice: "£1,350/month",
    rating: 4.5,
    reviewCount: 118,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–12:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Oxford Hawk is an independent lettings and property management specialist based on St Clements, serving the eastern and central parts of the city since 2011. With around 110 managed properties and an average rent of £1,350 per month, the team has built a strong reputation for hands-on service and local knowledge.\n\nThe branch covers East Oxford, Oxford City Centre, Cowley, Iffley, Headington, and Grandpont, areas where rental demand from students, young professionals, and families remains consistently high. Their focus on quality rental properties and responsive management has earned them a loyal landlord base.\n\nRated 4.5 stars from 118 reviews, Oxford Hawk is a modern, independent alternative for landlords and tenants who value a personal, proactive approach over the scale of a national chain.",
    whyChoose: [
      "Independent lettings specialist with a hands-on, personal approach",
      "Rated 4.5/5 from 118 client reviews",
      "Around 110 managed properties across East and Central Oxford",
      "Responsive management with strong local knowledge of the OX4 area",
    ],
    faq: [
      {
        question: "What areas does Oxford Hawk cover?",
        answer:
          "Oxford Hawk covers East Oxford, Oxford City Centre, Cowley, Iffley, Headington, and Grandpont, with a strong presence in the St Clements area and the surrounding streets.",
      },
      {
        question: "What services does Oxford Hawk offer?",
        answer:
          "Oxford Hawk specialises in lettings and property management. They offer full landlord services including tenant finding, referencing, rent collection, property inspections, and maintenance coordination.",
      },
      {
        question: "What are Oxford Hawk's opening hours?",
        answer:
          "Oxford Hawk is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 12:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "your-move-oxford",
    name: "Your Move",
    branch: "Oxford",
    address: "186 Cowley Road",
    postcode: "OX4 1UE",
    phone: "01865 792888",
    website: "https://www.your-move.co.uk/estate-agent/oxford",
    email: "oxford@your-move.co.uk",
    description:
      "Your Move Oxford is part of one of the UK's largest estate agency networks, with their Cowley Road office serving as a busy hub for east and south Oxford. They offer a comprehensive range of services including sales, lettings, mortgages, and conveyancing at all price points across the city.",
    established: 1982,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["East Oxford", "Cowley", "Iffley", "Rose Hill", "Littlemore", "Headington"],
    propertyCount: 95,
    avgPrice: "£380,000",
    rating: 4.0,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Your Move Oxford is part of one of the UK's largest estate agency networks, with their Cowley Road office serving east and south Oxford since 1982. Handling around 95 properties with an average price of £380,000, the team offers a comprehensive service covering sales, lettings, mortgages, and conveyancing at all price points.\n\nThe branch covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Headington, working with a wide range of clients from first-time buyers to investors. Their national network provides access to a large buyer database and in-house financial services, which can simplify the buying and selling process.\n\nWith a 4.0-star rating from 198 reviews, Your Move Oxford is a practical, cost-effective option for those looking to buy, sell, or rent in the more affordable parts of the city.",
    whyChoose: [
      "Part of one of the UK's largest estate agency networks",
      "Rated 4.0/5 from 198 client reviews",
      "Comprehensive service including sales, lettings, mortgages, and conveyancing",
      "Accessible pricing with average listings around £380,000",
    ],
    faq: [
      {
        question: "What areas does Your Move Oxford cover?",
        answer:
          "Your Move Oxford covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Headington, with a focus on the residential areas south and east of the city centre.",
      },
      {
        question: "What services does Your Move Oxford offer?",
        answer:
          "Your Move Oxford provides residential sales, lettings, property management, and new homes services. They also offer in-house mortgage advice and conveyancing to streamline the buying and selling process.",
      },
      {
        question: "What are Your Move Oxford's opening hours?",
        answer:
          "Your Move Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fine-country-oxford",
    name: "Fine & Country",
    branch: "Oxford",
    address: "290 Banbury Road, Summertown",
    postcode: "OX2 7ED",
    phone: "01865 861166",
    website: "https://www.fineandcountry.co.uk/oxford-estate-agents",
    email: "oxford@fineandcountry.com",
    description:
      "Fine & Country Oxford specialises in premium and luxury property across the city and its rural surroundings. Their Banbury Road office showcases some of Oxford's finest homes, with bespoke marketing including professional photography, video tours, and global reach through the Fine & Country international network.",
    established: 2003,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Relocation"],
    areas: ["North Oxford", "Summertown", "Jericho", "Wolvercote", "Oxford City Centre", "Headington"],
    propertyCount: 35,
    avgPrice: "£975,000",
    rating: 4.7,
    reviewCount: 62,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fine & Country Oxford specialises in premium and luxury property, with their Banbury Road office in Summertown showcasing some of the city's finest homes. Since 2003, the team has handled around 35 properties with an average listing price of £975,000, combining bespoke marketing with global reach through the Fine & Country international network.\n\nCovering North Oxford, Summertown, Jericho, Wolvercote, Oxford City Centre, and Headington, the branch offers residential sales, lettings, new homes, and relocation services. Their marketing stands out: professional photography, video tours, and high-quality brochures are standard, ensuring each property is presented to its full potential.\n\nRated 4.7 stars from 62 reviews, Fine & Country Oxford is the choice for sellers who want their property marketed with the care and sophistication it deserves, reaching qualified buyers both locally and internationally.",
    whyChoose: [
      "Rated 4.7/5 from 62 client reviews",
      "Bespoke marketing with professional photography and video tours",
      "International buyer network through the Fine & Country global platform",
      "Luxury focus with an average listing price of £975,000",
    ],
    faq: [
      {
        question: "What areas does Fine & Country Oxford cover?",
        answer:
          "Fine & Country Oxford covers North Oxford, Summertown, Jericho, Wolvercote, Oxford City Centre, and Headington, with additional reach into the premium Oxfordshire countryside market.",
      },
      {
        question: "What services does Fine & Country Oxford offer?",
        answer:
          "Fine & Country Oxford provides residential sales, lettings, new homes, and relocation services. Their bespoke marketing includes professional photography, video tours, and premium brochures for every instruction.",
      },
      {
        question: "What are Fine & Country Oxford's opening hours?",
        answer:
          "Fine & Country Oxford is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "andrews-oxford",
    name: "Andrews",
    branch: "Oxford",
    address: "108 London Road, Headington",
    postcode: "OX3 9AX",
    phone: "01865 766866",
    website: "https://www.andrewsonline.co.uk/branches/oxford",
    email: "oxford@andrewsonline.co.uk",
    description:
      "Andrews has a strong presence on London Road in Headington, offering full-service estate agency across Oxford. As a company that has been in the property business since 1946, they share knowledge and expertise across their branch network covering sales, lettings, mortgages, and conveyancing.",
    established: 1946,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Headington", "Marston", "Cowley", "East Oxford", "Littlemore", "Rose Hill"],
    propertyCount: 105,
    avgPrice: "£415,000",
    rating: 4.1,
    reviewCount: 234,
    hours: [
      { days: "Mon–Fri", time: "09:30–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Andrews has been a trusted name in property since 1946, and their Oxford office on London Road in Headington brings that experience to the eastern side of the city. With around 105 properties and an average price of £415,000, the team provides a full-service offering covering sales, lettings, property management, and new homes.\n\nThe branch covers Headington, Marston, Cowley, East Oxford, Littlemore, and Rose Hill, working across a broad price range from starter homes to family properties. Their extensive branch network means knowledge and expertise are shared across locations, benefiting buyers and sellers with wider market insight.\n\nWith a 4.1-star rating from 234 reviews and eight decades of experience, Andrews Oxford is a straightforward, reliable option for clients who value longevity, local knowledge, and a full-service approach.",
    whyChoose: [
      "Eight decades of property experience since 1946",
      "Rated 4.1/5 from 234 client reviews",
      "Full-service agency covering sales, lettings, mortgages, and conveyancing",
      "Strong branch network with shared local market knowledge",
    ],
    faq: [
      {
        question: "What areas does Andrews Oxford cover?",
        answer:
          "Andrews Oxford covers Headington, Marston, Cowley, East Oxford, Littlemore, and Rose Hill, with a focus on the residential areas along and around London Road.",
      },
      {
        question: "What services does Andrews Oxford offer?",
        answer:
          "Andrews Oxford provides residential sales, lettings, property management, and new homes services. They also offer mortgage advice and conveyancing through their wider branch network.",
      },
      {
        question: "What are Andrews Oxford's opening hours?",
        answer:
          "Andrews Oxford is open Monday to Friday from 09:30 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "leaders-oxford",
    name: "Leaders Letting & Estates",
    branch: "Oxford",
    address: "174 Cowley Road",
    postcode: "OX4 1UE",
    phone: "01865 790088",
    website: "https://www.leaders.co.uk/our-offices/oxfordshire/estate-agents-in-oxford/",
    email: "oxford@leaders.co.uk",
    description:
      "Leaders Oxford is part of one of the UK's largest lettings groups, with their Cowley Road office handling both sales and lettings across the city. With strong national infrastructure and local expertise, they cover everything from student accommodation to family homes across East Oxford and beyond.",
    established: 1983,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["East Oxford", "Cowley", "Iffley", "Rose Hill", "Littlemore", "Grandpont"],
    propertyCount: 90,
    avgPrice: "£395,000",
    rating: 4.1,
    reviewCount: 156,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Leaders Letting & Estates is part of one of the UK's largest lettings groups, and their Cowley Road office has been serving the Oxford market since 1983. With around 90 properties and an average price of £395,000, the team handles both sales and lettings across East Oxford and the surrounding areas.\n\nThe branch covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Grandpont, with particular strength in student accommodation and family homes. Their national infrastructure means access to a broad tenant and buyer database, while the local team brings hands-on knowledge of the Cowley Road corridor and its diverse communities.\n\nRated 4.1 stars from 156 reviews, Leaders Oxford is a solid choice for landlords, tenants, buyers, and sellers who want the backing of a national brand combined with a team that understands the east Oxford market.",
    whyChoose: [
      "Part of one of the UK's largest lettings groups, established 1983",
      "Rated 4.1/5 from 156 client reviews",
      "Strong lettings portfolio covering student and family homes",
      "National infrastructure with a locally knowledgeable Cowley Road team",
    ],
    faq: [
      {
        question: "What areas does Leaders Oxford cover?",
        answer:
          "Leaders Oxford covers East Oxford, Cowley, Iffley, Rose Hill, Littlemore, and Grandpont, with a strong presence along the Cowley Road corridor and the surrounding residential streets.",
      },
      {
        question: "What services does Leaders Oxford offer?",
        answer:
          "Leaders Oxford provides residential sales, lettings, and property management. Their team handles everything from property marketing and tenant referencing to rent collection and ongoing maintenance.",
      },
      {
        question: "What are Leaders Oxford's opening hours?",
        answer:
          "Leaders Oxford is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
]
